import api, { getApiErrors } from '@app/services/api';
import type {
    AIAgentAddAssignmentParams,
    AIAgentAddParams,
    AIAgentAddResponse,
    AIAgentDeleteParams,
    AIAgentDeleteResponse,
    AIAgentEditParams,
    AIAgentEditResponse,
    AIAgentListParams,
    ApiError,
} from '@app/services/api.types';
import { fn } from '@app/utils';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';

export const createAgent = async (params: AIAgentAddParams): Promise<AIAgentAddResponse> => {
    const url = apiEndpoints.aiAgents.add;
    const response = await api.post(url, params);
    return response?.data?.data;
};

export const createAssignment = async (
    params: AIAgentAddAssignmentParams
): Promise<AIAgentAddResponse> => {
    const url = apiEndpoints.aiAgents.assignNumber;
    const response = await api.post(url, params);
    return response?.data?.data;
};

export const updateAgent = async (params: AIAgentEditParams): Promise<AIAgentEditResponse> => {
    const url = apiEndpoints.aiAgents.update(params.id);
    const response = await api.put(url, params);
    return response?.data?.data;
};

export const removeAgent = async (params: AIAgentDeleteParams): Promise<AIAgentDeleteResponse> => {
    const url = apiEndpoints.aiAgents.delete(params.id);
    const response = await api.delete(url);
    return response?.data?.data;
};

export const exportAgents = async (params: AIAgentListParams) => {
    const url = apiEndpoints.aiAgents.getListExport(params);
    const response = await api.get<Blob>(url, {
        responseType: 'blob',
    });
    return response;
};

const useAgentActions = () => {
    const { message } = useApp();

    const addAgent = useMutation<AIAgentAddResponse, AxiosError<ApiError>, AIAgentAddParams>({
        mutationFn: createAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Agent added successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [
                ['ai-agent', 'list'],
                ['ai-number', 'list'],
            ],
        },
    });

    const editAgent = useMutation<AIAgentEditResponse, AxiosError<ApiError>, AIAgentEditParams>({
        mutationFn: updateAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Agent updated successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [
                ['ai-agent', 'list'],
                ['ai-number', 'list'],
            ],
        },
    });

    const deleteAgent = useMutation<
        AIAgentDeleteResponse,
        AxiosError<ApiError>,
        AIAgentDeleteParams
    >({
        mutationFn: removeAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Agent deleted successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [
                ['ai-agent', 'list'],
                ['ai-number', 'list'],
            ],
        },
    });

    const assignNumber = useMutation<
        AIAgentAddResponse,
        AxiosError<ApiError>,
        AIAgentAddAssignmentParams
    >({
        mutationFn: createAssignment,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Number assigned successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['ai-number', 'list']],
        },
    });

    const exportAgentsMutation = useMutation<
        AxiosResponse<Blob>,
        AxiosError<ApiError>,
        AIAgentListParams
    >({
        mutationFn: exportAgents,
        onSuccess: (res) => {
            const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
            const filename = match?.[1] || 'agents.csv';
            fn.downloadFileFromBlob({
                blob: res.data,
                fileName: filename,
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    return {
        addAgent,
        editAgent,
        deleteAgent,
        assignNumber,
        exportAgents: exportAgentsMutation,
    };
};

export default useAgentActions;
